/**
 * EIGOO Boarding URL Resolver · Director directive 2026.02 (Strike #14)
 *
 * Single source of truth for the "BOARD THE FLEET" / "BOARD THE BRIDGE"
 * / "BOARD THE DESK" CTA destination across the entire armada.
 *
 * Resolution rules:
 *   - Canonical domain (`eigooinc.net` / `www.eigooinc.net`)  → relative `/register`
 *   - Sovereign preview host                                 → relative `/register`
 *   - Local dev (`localhost`)                                 → relative `/register`
 *   - ANY subdomain of eigooinc.net (recruitment, hi, techops,
 *     trade, sports, cyberops, ...)                           → ABSOLUTE
 *                                                               `https://eigooinc.net/register`
 *
 * The absolute URL on subdomains fixes the previous 404 where
 * `recruitment.eigooinc.net/register` had no React route serving it.
 *
 * If `/recruitment` ever becomes a real page on the canonical domain,
 * flip BOARDING_PATH below to `"/recruitment"` and the entire fleet
 * updates with a single redeploy.
 */
import React from "react";
import { Link } from "react-router-dom";

export const BOARDING_HOST = "eigooinc.net";
export const BOARDING_PATH = "/register";

/** Hosts that should keep relative-path navigation (no cross-origin hop). */
function isCanonicalHost(host) {
    if (!host) return false;
    if (host === BOARDING_HOST || host === `www.${BOARDING_HOST}`) return true;
    if (host === "localhost" || host === "127.0.0.1") return true;
    if (host.endsWith(".emergentagent.com")) return true;
    return false;
}

/**
 * Returns the canonical boarding URL appropriate for the *current* host.
 *  - SSR-safe: returns relative path when `window` is unavailable.
 *  - On a subdomain, returns the absolute canonical URL.
 */
export function getBoardingUrl() {
    if (typeof window === "undefined") return BOARDING_PATH;
    const host = window.location.hostname;
    if (isCanonicalHost(host)) return BOARDING_PATH;
    return `https://${BOARDING_HOST}${BOARDING_PATH}`;
}

/** Hook-friendly accessor for components that want a stable value. */
export function useBoardingUrl() {
    return getBoardingUrl();
}

/**
 * Drop-in CTA element. Renders <Link to="/register"> on canonical hosts
 * (SPA navigation, no full page reload) and <a href="https://eigooinc.net/register">
 * on subdomains (cross-origin hop required).
 *
 * Forward all other props (className, data-testid, style, onMouseEnter,
 * onClick, children) verbatim.
 */
export function BoardLink({ children, ...rest }) {
    const url = getBoardingUrl();
    if (url.startsWith("http")) {
        return (
            <a href={url} {...rest}>
                {children}
            </a>
        );
    }
    return (
        <Link to={url} {...rest}>
            {children}
        </Link>
    );
}

